import type { ApiLevel, ApiPractice, ApiPracticeLevel } from '@/types/api'

// Rows are labelled with the translated `displayName`, but ordering must stay
// language-invariant: sortOrder first, then the canonical (English) `name` as a
// deterministic tiebreak. Collation is pinned to 'en' so the order does not shift
// with the active UI language.
export function byCanonicalOrder(
  a: { sortOrder: number; name: string },
  b: { sortOrder: number; name: string }
): number {
  if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder
  return a.name.localeCompare(b.name, 'en', { numeric: true, sensitivity: 'base' })
}

export interface PracticeLevelGroup {
  /** Null for practices with no level assignment. */
  level: ApiLevel | null
  practices: ApiPractice[]
}

/**
 * Groups practices under their PRIMARY level (the lowest-sequence level they belong to), so each
 * practice appears under exactly one level. Groups are ordered by level sequence; unassigned
 * practices come last in a `level: null` group.
 */
export function groupPracticesByPrimaryLevel(
  practices: ApiPractice[],
  practiceLevels: ApiPracticeLevel[],
  levels: ApiLevel[]
): PracticeLevelGroup[] {
  const levelById = new Map(levels.map((l) => [l.id, l]))
  const levelIdsByPractice = new Map<number, number[]>()
  for (const pl of practiceLevels) {
    const arr = levelIdsByPractice.get(pl.practiceId)
    if (arr) arr.push(pl.levelId)
    else levelIdsByPractice.set(pl.practiceId, [pl.levelId])
  }

  const groups = new Map<number | null, ApiPractice[]>()
  for (const p of practices) {
    let primary: ApiLevel | null = null
    for (const levelId of levelIdsByPractice.get(p.id) ?? []) {
      const level = levelById.get(levelId)
      if (level && (primary === null || level.sequence < primary.sequence)) primary = level
    }
    const key = primary?.id ?? null
    const arr = groups.get(key)
    if (arr) arr.push(p)
    else groups.set(key, [p])
  }

  return [...groups]
    .map(([levelId, groupPractices]) => ({
      level: levelId === null ? null : (levelById.get(levelId) ?? null),
      practices: groupPractices.sort(byCanonicalOrder),
    }))
    .sort((a, b) =>
      a.level === null ? 1 : b.level === null ? -1 : a.level.sequence - b.level.sequence
    )
}
